import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Calendar from '../components/Calendar';
import TimeSlotSelector from '../components/TimeSlotSelector';
import BookingForm from '../components/BookingForm';
import { useAuthStore } from '../store/authStore';
import { FiArrowLeft, FiCheck, FiX } from 'react-icons/fi';

const BookingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();

  const [bookingType, setBookingType] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [error, setError] = useState(null);
  const [autoAccept, setAutoAccept] = useState(false);

  // Leggi il tipo dalla URL
  useEffect(() => {
    if (location.pathname === '/prenota-carico') {
      setBookingType('carico');
    } else if (location.pathname === '/prenota-scarico') {
      setBookingType('scarico');
    }
  }, [location.pathname]);

  // Redirect se non autenticato
  if (!user) {
    navigate('/login');
    return null;
  }

  const handleBookingTypeSelect = (type) => {
    setBookingType(type);
    setSelectedDate(null);
    setSelectedSlot(null);
    setError(null);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
  };

  const handleSubmitBooking = async (formData) => {
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/appointments/book`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setConfirmation(response.data.appointment);
      const autoAcceptValue = response.data.autoAccept || response.data.autoAccept === false ? response.data.autoAccept : false;
      setAutoAccept(autoAcceptValue);
      console.log('✓ Booking response:', { autoAccept: autoAcceptValue, message: response.data.message });
      setBookingType(null);
      setSelectedDate(null);
      setSelectedSlot(null);

      // Nascondi il messaggio di conferma dopo 5 secondi
      setTimeout(() => {
        setConfirmation(null);
        navigate('/dashboard-cliente');
      }, 5000);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Errore durante la prenotazione';
      setError(errorMsg);
      console.error('Booking error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Messaggio di conferma
  if (confirmation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <FiCheck size={32} className="text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {autoAccept ? 'Prenotazione Confermata!' : 'Richiesta Inviata!'}
          </h2>
          <p className="text-gray-600 mb-4">
            {new Date(confirmation.date).toLocaleDateString('it-IT')} alle {confirmation.timeSlot}
          </p>
          <p className="text-sm text-gray-500">
            {autoAccept 
              ? 'La tua prenotazione è confermata.'
              : 'La tua richiesta è stata inviata. L\'amministratore la esaminerà a breve.'}
          </p>
          <p className="text-xs text-gray-400 mt-4">
            Reindirizzamento alla dashboard in 5 secondi...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Logo - Solo Mobile */}
        <div className="md:hidden flex justify-center mb-8">
          <img 
            src="/images/logo.svg" 
            alt="EcoPrenota Logo" 
            className="h-16 object-contain"
          />
        </div>

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/dashboard-cliente')}
            className="p-2 hover:bg-white rounded-lg transition"
          >
            <FiArrowLeft size={24} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">
              Prenota {bookingType === 'carico' ? '📦 Carico' : '📥 Scarico'}
            </h1>
            <p className="text-gray-600">Benvenuto, {user.companyName}</p>
          </div>
        </div>

        {/* Booking flow - Mostra sempre senza selezione del tipo */}
        {bookingType && (
          <div className="grid md:grid-cols-3 gap-6">
            {/* Colonna 1: Calendario */}
            <div>
              <h2 className="text-lg font-bold text-gray-800 mb-4">📅 Data</h2>
              <Calendar onDateSelect={handleDateSelect} selectedDate={selectedDate} />
            </div>

            {/* Colonna 2: Selezione slot */}
            <div>
              <h2 className="text-lg font-bold text-gray-800 mb-4">🕐 Orario</h2>
              <TimeSlotSelector
                date={selectedDate}
                type={bookingType}
                onSlotSelect={handleSlotSelect}
                selectedSlot={selectedSlot}
              />
            </div>

            {/* Colonna 3: Form prenotazione */}
            <div>
              <h2 className="text-lg font-bold text-gray-800 mb-4">📝 Dettagli</h2>
              <BookingForm
                date={selectedDate}
                timeSlot={selectedSlot}
                type={bookingType}
                onSubmit={handleSubmitBooking}
                isLoading={isLoading}
                user={user}
              />
            </div>
          </div>
        )}

        {/* Messaggio di errore */}
        {error && (
          <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center gap-2">
            <FiX size={20} />
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-4 font-bold text-red-700 hover:text-red-900"
            >
              ✕
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingPage;
